import { useEffect, useRef, useState, type FC } from "react";

import { LoadingState } from "@/components/brand";
import { cn } from "@/lib/utils";

import { useCaseData } from "../data/CaseDataContext";

/** How long the migrate request may take before the overlay shows at all. */
export const REVEAL_DELAY_MS = 400;
/** Matches the `duration-150` fade-out below. */
const FADE_OUT_MS = 150;

export const UPGRADE_TITLE = "Upgrading case workspace";

type Phase = "hidden" | "shown" | "closing";

/**
 * Drive the overlay's phase from `active`: reveal after a delay so cases that
 * need no upgrade never flash it, and hold for one fade-out when it ends.
 */
export const useRevealPhase = (
  active: boolean,
  revealDelayMs = REVEAL_DELAY_MS,
  fadeOutMs = FADE_OUT_MS
): Phase => {
  const [phase, setPhase] = useState<Phase>("hidden");
  // Read inside the effect without being a dependency: re-running on the
  // "closing" -> "hidden" transition would restart the fade-out timer.
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;

  useEffect(() => {
    if (active) {
      const timer = window.setTimeout(() => setPhase("shown"), revealDelayMs);
      return () => window.clearTimeout(timer);
    }
    if (phaseRef.current === "shown") {
      setPhase("closing");
      const timer = window.setTimeout(() => setPhase("hidden"), fadeOutMs);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [active, revealDelayMs, fadeOutMs]);

  return phase;
};

type Props = {
  /** Overrides the provider's flag; for stories and tests. */
  active?: boolean;
  className?: string;
};

/**
 * The migration's own loading state. Covers the IDE panes (not the case
 * header) while the one-time legacy upgrade runs; the panes keep loading
 * underneath, blurred.
 */
export const CaseUpgradeOverlay: FC<Props> = ({ active, className }) => {
  const { compat } = useCaseData();
  const phase = useRevealPhase(active ?? compat.isUpgrading);
  if (phase === "hidden") return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={phase === "shown"}
      data-testid="case-upgrade-overlay"
      data-state={phase === "closing" ? "closed" : "open"}
      className={cn(
        "pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm",
        phase === "closing"
          ? "animate-out fade-out-0 duration-150"
          : "animate-in fade-in-0 duration-200",
        className
      )}
    >
      <LoadingState size="md" label={UPGRADE_TITLE} />
    </div>
  );
};
